import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PaperPlaneRight } from "@phosphor-icons/react";
import api, { formatErr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function TeacherMessages() {
  const { t } = useTranslation();
  const [scope, setScope] = useState({ classes: [], purposes: [] });
  const [classId, setClassId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [purpose, setPurpose] = useState("General");
  const [text, setText] = useState("");
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadSent = () =>
    api
      .get("/teacher/messages")
      .then((r) => setSent(r.data))
      .catch((e) => toast.error(formatErr(e)));

  useEffect(() => {
    api
      .get("/teacher/messages/scope")
      .then((r) => setScope(r.data))
      .catch((e) => toast.error(formatErr(e)));
    loadSent();
  }, []);

  useEffect(() => {
    setStudentId("");
    setSubjectId("");
    setStudents([]);
    if (classId) {
      api
        .get("/teacher/messages/students", { params: { class_id: classId } })
        .then((r) => setStudents(r.data))
        .catch((e) => toast.error(formatErr(e)));
    }
  }, [classId]);

  const cls = scope.classes.find((c) => c.id === classId);
  const selectedStudent = students.find((s) => s.id === studentId);

  const send = async () => {
    if (!classId || !studentId || !text.trim()) {
      toast.error(
        t("teacherMessages.validation", {
          defaultValue: "Select a class and student, and write a message",
        })
      );
      return;
    }

    setBusy(true);
    try {
      await api.post("/teacher/messages", {
        class_id: classId,
        subject_id: subjectId || null,
        student_id: studentId,
        purpose,
        message: text.trim(),
      });

      toast.success(
        t("teacherMessages.sentSuccess", {
          defaultValue: "Message sent to the parent",
        })
      );

      setText("");
      setStudentId("");
      setSubjectId("");
      loadSent();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="teacher-messages" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">
          {t("teacherMessages.heading", { defaultValue: "Parent Messages" })}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {t("teacherMessages.description", {
            defaultValue:
              "Send a private one-way message to a student's linked parent. You can only message students in classes assigned to you.",
          })}
        </p>
      </div>

      <div
        className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4"
        data-testid="tm-compose"
      >
        {scope.classes.length === 0 ? (
          <p
            className="text-sm text-slate-400"
            data-testid="tm-no-scope"
          >
            {t("teacherMessages.noScope", {
              defaultValue:
                "You are not assigned to any class yet. Ask your School Admin to make you a class teacher or assign you a subject for a class.",
            })}
          </p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>
                  {t("teacherMessages.class", { defaultValue: "Class" })}
                </Label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger
                    data-testid="tm-class-select"
                    className="mt-1.5 bg-white"
                  >
                    <SelectValue
                      placeholder={t("teacherMessages.selectClass", {
                        defaultValue: "Select class",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {scope.classes.map((c) => (
                      <SelectItem
                        key={c.id}
                        value={c.id}
                        data-testid={`tm-class-option-${c.id}`}
                      >
                        {c.name}
                        {c.is_class_teacher
                          ? ` (${t("teacherMessages.classTeacher", {
                              defaultValue: "Class Teacher",
                            })})`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>
                  {t("teacherMessages.subject", { defaultValue: "Subject" })}{" "}
                  {cls && !cls.is_class_teacher
                    ? ""
                    : `(${t("teacherMessages.optional", {
                        defaultValue: "optional",
                      })})`}
                </Label>
                <Select
                  value={subjectId}
                  onValueChange={setSubjectId}
                  disabled={!classId}
                >
                  <SelectTrigger
                    data-testid="tm-subject-select"
                    className="mt-1.5 bg-white"
                  >
                    <SelectValue
                      placeholder={
                        cls?.subjects.length
                          ? t("teacherMessages.selectSubject", {
                              defaultValue: "Select subject",
                            })
                          : t("teacherMessages.noSubjectNeeded", {
                              defaultValue: "No subject needed",
                            })
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(cls?.subjects || []).map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        data-testid={`tm-subject-option-${s.id}`}
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>
                  {t("teacherMessages.student", { defaultValue: "Student" })}
                </Label>
                <Select
                  value={studentId}
                  onValueChange={setStudentId}
                  disabled={!classId}
                >
                  <SelectTrigger
                    data-testid="tm-student-select"
                    className="mt-1.5 bg-white"
                  >
                    <SelectValue
                      placeholder={t("teacherMessages.selectStudent", {
                        defaultValue: "Select student",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        data-testid={`tm-student-option-${s.id}`}
                        disabled={!s.has_parent}
                      >
                        {s.name}
                        {s.roll_no ? ` · ${s.roll_no}` : ""}
                        {s.has_parent
                          ? ""
                          : ` (${t("teacherMessages.noLinkedParent", {
                              defaultValue: "no linked parent",
                            })})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>
                  {t("teacherMessages.purpose", { defaultValue: "Purpose" })}
                </Label>
                <Select value={purpose} onValueChange={setPurpose}>
                  <SelectTrigger
                    data-testid="tm-purpose-select"
                    className="mt-1.5 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {scope.purposes.map((p) => (
                      <SelectItem
                        key={p}
                        value={p}
                        data-testid={`tm-purpose-option-${p}`}
                      >
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>
                {t("teacherMessages.message", { defaultValue: "Message" })}
              </Label>
              <Textarea
                data-testid="tm-message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={2000}
                rows={4}
                className="mt-1.5 bg-white"
                placeholder={t("teacherMessages.messagePlaceholder", {
                  defaultValue:
                    "Write a clear, private message for this student's parent…",
                })}
              />
            </div>

            {selectedStudent && !selectedStudent.has_parent && (
              <p
                className="text-xs font-semibold text-amber-700 bg-status-amber/20 rounded-xl px-4 py-2.5"
                data-testid="tm-no-parent-warning"
              >
                {t("teacherMessages.noParentWarning", {
                  defaultValue:
                    "This student has no linked parent account — the school office should link one first.",
                })}
              </p>
            )}

            <Button
              data-testid="tm-send-btn"
              onClick={send}
              disabled={busy || !classId || !studentId || !text.trim()}
              className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-6 transition-transform duration-300 active:scale-95"
            >
              <PaperPlaneRight size={16} className="mr-1.5" />{" "}
              {busy
                ? t("teacherMessages.sending", { defaultValue: "Sending…" })
                : t("teacherMessages.sendToParent", {
                    defaultValue: "Send to parent",
                  })}
            </Button>
          </>
        )}
      </div>

      <div>
        <h2 className="font-heading font-bold mb-3">
          {t("teacherMessages.sentHistory", { defaultValue: "Sent history" })}
        </h2>

        <div className="space-y-2.5" data-testid="tm-sent-list">
          {sent.map((m) => (
            <div
              key={m.id}
              data-testid={`tm-row-${m.id}`}
              className="bg-white border border-slate-200 rounded-2xl p-4"
            >
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mb-1.5">
                <span className="font-bold text-slate-700 text-sm">
                  {m.student_name}
                </span>
                <span>
                  {m.class_name}
                  {m.subject_name ? ` · ${m.subject_name}` : ""}
                </span>
                <span className="bg-[#F4F5F7] rounded-full px-2.5 py-0.5 font-semibold">
                  {m.purpose}
                </span>
                <span className="ml-auto">{fmtDate(m.created_at)}</span>
              </div>

              <p className="text-sm text-slate-600 whitespace-pre-wrap">
                {m.message}
              </p>

              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-slate-400">
                  {t("teacherMessages.to", { defaultValue: "To" })}:{" "}
                  {m.parent_name}
                </span>

                <span
                  data-testid={`tm-read-${m.id}`}
                  className={`font-bold px-2.5 py-0.5 rounded-full ${
                    m.is_read
                      ? "bg-status-green/10 text-status-green"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {m.is_read
                    ? t("teacherMessages.read", { defaultValue: "Read" })
                    : t("teacherMessages.unread", { defaultValue: "Unread" })}
                </span>
              </div>
            </div>
          ))}

          {sent.length === 0 && (
            <p
              className="text-sm text-slate-400 py-6 text-center"
              data-testid="tm-sent-empty"
            >
              {t("teacherMessages.noMessagesSent", {
                defaultValue: "No messages sent yet.",
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
